/**
 * Click a line, see where the customer wrote it (360 simulation P28).
 *
 * On the case page the mail card sits above the draft table. Choosing a line marks the
 * text that line was read from in the mail; a click on the customer's words in a row
 * also brings that text into view (the page scrolls, or the nearest scroll box when the
 * card sits in one), so each line can be checked against the mail without searching.
 * The text is found by what the line says (the request text, then its start, then the
 * customer's item number or the longest code in it); the mail is never changed, the mark
 * is a CSS highlight that goes with the next pick.
 *
 * The card folds the mail to six lines ("Read all"): when the text sits in the folded
 * part, `focusMailText` says so ("clipped") and the card unfolds and asks again, so the
 * mark is never set behind the fold on a click that is meant to reveal it.
 */

/** The CSS highlight name; styled in styles/globals.css. */
export const MAIL_LINE_HIGHLIGHT = "i2q-mail-line";

/** Marks a mail block whose text is folded (`mail-column.tsx` sets it while clamped). */
export const MAIL_CLAMPED_ATTR = "data-mail-clamped";

const NEEDLE_MIN = 4;
const START_CHARS = 40;

/** Lower case, one space between words, typographic quotes as plain ones. */
export function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, " ")
    .trim();
}

/** What to look for in the mail for one line, most specific first (normalized, unique). */
export function focusNeedles(
  enquiryText: string | null | undefined,
  customerItem?: string | null,
): string[] {
  const text = normalizeText(enquiryText ?? "");
  const out: string[] = [];
  const add = (value: string | null | undefined) => {
    const needle = normalizeText(value ?? "");
    if (needle.length >= NEEDLE_MIN && !out.includes(needle)) {
      out.push(needle);
    }
  };
  add(text);
  if (text.length > START_CHARS) {
    const cut = text.slice(0, START_CHARS);
    add(cut.slice(0, Math.max(cut.lastIndexOf(" "), NEEDLE_MIN)));
  }
  add(customerItem);
  // The longest token with a digit: an item or drawing number survives any rewording.
  const codes = text
    .split(" ")
    .map((token) => token.replace(/^[^\w]+|[^\w]+$/g, ""))
    .filter((token) => /\d/.test(token))
    .sort((a, b) => b.length - a.length);
  add(codes[0]);
  return out;
}

/**
 * Where `needle` (normalized) stands in `haystack` (raw text), as raw `[start, end)`
 * offsets; null when it is not there. Whitespace runs and case do not matter.
 */
export function locate(haystack: string, needle: string): [number, number] | null {
  if (!needle) {
    return null;
  }
  // The normalized text with, for each of its characters, the raw offset it came from.
  let normalized = "";
  const rawAt: number[] = [];
  let lastSpace = true;
  for (let i = 0; i < haystack.length; i += 1) {
    const raw = haystack[i] ?? "";
    if (/\s/.test(raw)) {
      if (!lastSpace) {
        normalized += " ";
        rawAt.push(i);
        lastSpace = true;
      }
      continue;
    }
    const char = normalizeText(raw) || raw.toLowerCase();
    normalized += char;
    rawAt.push(i);
    lastSpace = false;
  }
  const at = normalized.indexOf(needle);
  if (at < 0) {
    return null;
  }
  const start = rawAt[at] ?? 0;
  const end = (rawAt[at + needle.length - 1] ?? start) + 1;
  return [start, end];
}

/** The nearest ancestor that scrolls vertically, else null (the page itself scrolls then). */
function scrollParent(node: Element | null): HTMLElement | null {
  for (let el = node?.parentElement ?? null; el; el = el.parentElement) {
    const style = window.getComputedStyle(el);
    if (/(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight) {
      return el;
    }
  }
  return null;
}

/**
 * Bring `box` (a viewport rectangle) to the upper third of its scroll box, or of the
 * window when no ancestor scrolls: the case page is one tall column by design (the
 * action bar is sticky to the viewport), so the window is the usual case.
 */
function scrollTo(box: DOMRect, scroller: HTMLElement | null): void {
  if (scroller) {
    const frame = scroller.getBoundingClientRect();
    scroller.scrollTo({
      top: scroller.scrollTop + box.top - frame.top - frame.height / 3,
      behavior: "smooth",
    });
    return;
  }
  window.scrollBy({ top: box.top - window.innerHeight / 3, behavior: "smooth" });
}

/** True when `box` lies below the visible part of the folded block `clamped`. */
function hiddenByFold(box: DOMRect, clamped: Element | null): boolean {
  if (!clamped) {
    return false;
  }
  return box.bottom > clamped.getBoundingClientRect().bottom + 1;
}

export interface MailFocusOptions {
  /**
   * Bring the text into view (a click on the customer's words). False marks it only:
   * a row opened by its chevron or focused by the action bar keeps the page where the
   * person is working, and the mark waits in the mail for when they look up.
   */
  scroll?: boolean;
}

/**
 * "marked": the text is marked (and in view when asked). "clipped": the text sits in
 * the folded part of the mail; it is marked, but the card has to unfold before a scroll
 * can show it. "missing": none of the needles is in the mail.
 */
export type MailFocusResult = "marked" | "clipped" | "missing";

/**
 * Mark the first needle found in the mail under `root`, and scroll it into view when
 * asked. Reads the DOM only; never changes the mail's nodes.
 */
export function focusMailText(
  root: HTMLElement | null,
  needles: readonly string[],
  { scroll = true }: MailFocusOptions = {},
): MailFocusResult {
  const highlights = (globalThis.CSS as { highlights?: Map<string, unknown> } | undefined)
    ?.highlights;
  highlights?.delete(MAIL_LINE_HIGHLIGHT);
  if (!root || needles.length === 0) {
    return "missing";
  }
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const texts: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    texts.push(node as Text);
  }
  for (const needle of needles) {
    for (const node of texts) {
      const found = locate(node.data, needle);
      if (!found) {
        continue;
      }
      const range = document.createRange();
      range.setStart(node, found[0]);
      range.setEnd(node, found[1]);
      const Highlight = (globalThis as { Highlight?: new (...ranges: Range[]) => unknown })
        .Highlight;
      if (highlights && Highlight) {
        // The mark stays on the text node when the card unfolds: React keeps the node.
        highlights.set(MAIL_LINE_HIGHLIGHT, new Highlight(range));
      }
      const box = range.getBoundingClientRect();
      const clamped = node.parentElement?.closest(`[${MAIL_CLAMPED_ATTR}]`) ?? null;
      if (hiddenByFold(box, clamped)) {
        return "clipped";
      }
      if (scroll) {
        scrollTo(box, scrollParent(node.parentElement));
      }
      return "marked";
    }
  }
  return "missing";
}
